import React, { useMemo, useCallback, useEffect } from 'react';
import { StyleSheet, Pressable, View } from 'react-native';
import Text from '@/components/Typography/Text';
import { useModalStore, useAuthStore, useCreditPacksStore } from '@/store';
import ChatInputBanner, { getWarningLinkColor } from '@/components/ChatInput/ChatInputBanner';
import useResponsiveness from '@/hooks/useResponsiveness';
import Button from '@/components/Buttons/Button';
import { useTheme } from '@/context/ThemeContext';
import { PLUS_COLORS } from '@/constants/Colors';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import {
  trackConversionEvent,
  setCheckoutSourceDimension,
  CONVERSION_ACTION,
  eventName,
} from '@/constants/utils/analyticsEvents';
import { trackEvent } from '@/constants/utils/analytics';
import useTranslation from '@/hooks/useTranslation';
import { formatCreditResetDate, startAryaChat } from '@/lib/plusCreditOptions';
import { formatCreditBalance } from '@/lib/formatCredits';

const BANNER_SURFACE = 'chat_low_credits_banner';

const createStyles = (linkColor) => StyleSheet.create({
  link: {
    marginLeft: 4,
    display: 'inline',
    textDecorationLine: 'underline',
    color: linkColor,
  },
  quickTopUpContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginLeft: 8,
  },
  cardInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
});

function ChatInputLowCreditsBanner({ level, isCloseable = true, isDetached = false }) {
  const { colors, theme } = useTheme();
  const warningLinkColor = useMemo(() => getWarningLinkColor(theme), [theme]);
  const styles = useMemo(() => createStyles(warningLinkColor), [warningLinkColor]);
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const totalAvailableCredits = useAuthStore(state => state.user?.credits?.totalAvailable ?? 0);
  const creditsLabel = formatCreditBalance(totalAvailableCredits);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isEmailVerified = useAuthStore(state => state.user?.flags?.isEmailVerified);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  // Server-computed: credits this user would still receive by verifying their
  // email (signup verification top-up). 0 for legacy unverified users who
  // already got the full grant — they fall through to the standard upsell.
  const pendingVerificationCredits = useAuthStore(state => state.user?.credits?.pendingVerificationCredits ?? 0);
  const hasUser = useAuthStore(state => Boolean(state.user?.id));
  const resetDate = useAuthStore(state => state.user?.credits?.monthlyAllotment?.resetDate);
  const resetLabel = useMemo(() => formatCreditResetDate(resetDate), [resetDate]);
  
  // Check if user has stored payment method for quick top-up (US only)
  const hasPaymentMethod = useAuthStore(state => state.user?.valmar?.hasPaymentMethod);
  const maskedCardData = useAuthStore(state => state.user?.valmar?.maskedCardData);
  const { usesWhopCheckout } = useGabPurchase();
  
  // Get the most popular credit pack for quick top-up
  const creditPacks = useCreditPacksStore(state => state.creditPacks);
  const popularPack = useMemo(() => {
    if (!creditPacks?.length) return null;
    return creditPacks.find(p => p.flags?.isPopular) || creditPacks[0];
  }, [creditPacks]);

  // Can use quick top-up if has payment method and card data (US only)
  const canQuickTopUp = !usesWhopCheckout && hasPaymentMethod && maskedCardData?.maskedNumber;

  // Get last 4 digits of card
  const cardLastFour = maskedCardData?.maskedNumber?.slice(-4) || '****';

  useEffect(() => {
    trackConversionEvent(
      CONVERSION_ACTION.UPGRADE_BANNER_SHOWN,
      eventName(BANNER_SURFACE, isPlus ? 'plus' : 'free'),
      totalAvailableCredits,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The banner renders one of six variants, and which CTA a visitor took is
  // the whole question — so the variant travels in the event name rather than
  // collapsing every path into one "clicked".
  const trackBannerClick = useCallback((cta) => {
    trackConversionEvent(
      CONVERSION_ACTION.LOW_CREDITS_BANNER_CLICKED,
      eventName(BANNER_SURFACE, cta),
      totalAvailableCredits,
    );
    setCheckoutSourceDimension(BANNER_SURFACE);
  }, [totalAvailableCredits]);

  const handleUpgradePress = useCallback(() => {
    trackBannerClick('upgrade');
    openModal('PricingModal', {
      title: t('chatInput.credits.getFreeMessagesTitle'),
      subtitle: t('chatInput.credits.getFreeMessagesSubtitle'),
    });
  }, [openModal, t, trackBannerClick]);

  // Logged-out visitors don't have a credit balance to "run out" of — the
  // honest CTA is to create an account, not to upgrade or buy credits.
  const handleSignIn = useCallback(() => {
    trackBannerClick('sign_in');
    openModal('AuthCTAModal', {
      source: 'chat_low_credits_banner',
      title: t('chatInput.credits.signInToCreate') || 'Sign in to start creating',
      subtitle: t('chatInput.credits.signInToCreateSubtitle') || 'Create a free account to chat, generate images, and use every tool.',
      icon: 'sparkles',
    });
  }, [openModal, t, trackBannerClick]);

  const handleQuickTopUp = useCallback(() => {
    trackBannerClick('quick_top_up');
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
  }, [openModal, trackBannerClick]);

  const handleGetCredits = useCallback(() => {
    trackBannerClick('get_credits');
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
  }, [openModal, trackBannerClick]);
  const handleVerifyEmail = useCallback(() => {
    trackBannerClick('verify_email');
    openModal('VerifyEmailModal', { source: 'low_credits_banner' });
  }, [openModal, trackBannerClick]);

  // Plus at zero: the modal lays out every choice (Arya free / buy / Premium /
  // refresh date) instead of jumping straight to the credit store.
  const handleSeePlusOptions = useCallback(() => {
    trackEvent('conversion', 'low_credits_banner_see_options', 'plus');
    openModal('OutOfCreditsModal', { source: 'chat_banner' });
  }, [openModal]);

  const handleChatWithArya = useCallback(() => {
    startAryaChat({ source: 'chat_banner' });
  }, []);

  // Anonymous visitors: a sign-in CTA instead of credit/upgrade messaging.
  if (!hasUser) {
    return (
      <ChatInputBanner
        color="upgrade"
        level={level}
        isCloseable={isCloseable}
        isDetached={isDetached}
      >
        {isXS
          ? (t('chatInput.credits.signInToCreateShort') || 'Sign in to start creating')
          : (t('chatInput.credits.signInToCreate') || 'Sign in to start creating for free')}
        <Pressable onPress={handleSignIn}>
          <Text size='s' weight='medium' style={[styles.link, { color: PLUS_COLORS.textColor }]}>
            {t('common.signIn') || 'Sign in'}
          </Text>
        </Pressable>
      </ChatInputBanner>
    );
  }

  // Unverified free signups start with a reduced grant. Never call that
  // "running low" — ask them to verify and unlock the rest instead.
  if (!isPlus && !isGhost && !isEmailVerified) {
    const bonus = pendingVerificationCredits;
    const message = bonus <= 0
      ? t('chatInput.credits.verifyToStart')
      : totalAvailableCredits <= 0
      ? (isXS
        ? t('chatInput.credits.verifyOutOfCreditsShort').replace('%BONUS%', bonus)
        : t('chatInput.credits.verifyOutOfCredits').replace('%BONUS%', bonus))
      : (isXS
        ? t('chatInput.credits.verifyForCreditsShort').replace('%BONUS%', bonus)
        : t('chatInput.credits.verifyForCredits').replace('%BONUS%', bonus));

    return (
      <ChatInputBanner
        color="upgrade"
        level={level}
        isCloseable={isCloseable}
        isDetached={isDetached}
      >
        {message}
        <Pressable onPress={handleVerifyEmail}>
          <Text size='s' weight='medium' style={[styles.link, { color: PLUS_COLORS.textColor }]}>
            {t(bonus > 0 ? 'chatInput.credits.verifyEmailLink' : 'chatInput.credits.verifyToStartLink')}
          </Text>
        </Pressable>
      </ChatInputBanner>
    );
  }

  // If Plus user with stored card, show quick top-up option when low
  if (isPlus && canQuickTopUp && totalAvailableCredits > 0) {
    return (
      <ChatInputBanner 
        color="warning" 
        level={level} 
        isCloseable={isCloseable} 
        isDetached={isDetached}
      >
        {isXS 
          ? t('chatInput.credits.creditsLeft').replace('%COUNT%', creditsLabel) 
          : t('chatInput.credits.runningLow').replace('%COUNT%', creditsLabel)
        }
        {!isXS && popularPack && (
          <View style={styles.quickTopUpContainer}>
            <Button
              variant="primary"
              text={t('chatInput.credits.getCredits')}
              size="xsmall"
              onPress={handleQuickTopUp}
            />
          </View>
        )}
        {isXS && (
          <Pressable onPress={handleQuickTopUp}>
            <Text size='s' weight='medium' style={styles.link}>
              {t('chatInput.credits.getCreditsLink')}
            </Text>
          </Pressable>
        )}
      </ChatInputBanner>
    );
  }

  // Plus at zero: say when credits come back, offer Arya for free in one tap,
  // and hand off to the full choice modal (buy / Premium) for everything else.
  if (isPlus) {
    if (totalAvailableCredits <= 0) {
      const message = isXS
        ? t('chatInput.credits.outOfCreditsShort')
        : (resetLabel
          ? t('plusOutOfCredits.bannerOutOfCredits', { date: resetLabel })
          : t('plusOutOfCredits.bannerOutOfCreditsNoDate'));
      return (
        <ChatInputBanner 
          color="danger" 
          level={level} 
          isCloseable={isCloseable} 
          isDetached={isDetached}
        >
          {message}
          <Pressable onPress={handleChatWithArya} accessibilityRole="button">
            <Text size='s' weight='medium' style={styles.link}>
              {t('plusOutOfCredits.bannerChatArya')}
            </Text>
          </Pressable>
          {!isXS && (
            <Text size='s' color='secondary' style={{ marginLeft: 4 }}>·</Text>
          )}
          <Pressable onPress={handleSeePlusOptions} accessibilityRole="button">
            <Text size='s' weight='medium' style={styles.link}>
              {isXS ? t('chatInput.credits.getCreditsLink') : t('plusOutOfCredits.bannerSeeOptions')}
            </Text>
          </Pressable>
        </ChatInputBanner>
      );
    }
    return null;
  }

  // Free user with stored card - show quick top-up option
  if (canQuickTopUp) {
    const getMessage = () => {
      if (totalAvailableCredits <= 0) {
        return isXS 
          ? t('chatInput.credits.outOfCreditsShort') 
          : t('chatInput.credits.outOfCredits');
      }
      return isXS 
        ? t('chatInput.credits.creditsLeft').replace('%COUNT%', creditsLabel) 
        : t('chatInput.credits.runningLow').replace('%COUNT%', creditsLabel);
    };

    return (
      <ChatInputBanner 
        color={totalAvailableCredits <= 0 ? "danger" : "warning"} 
        level={level} 
        isCloseable={isCloseable} 
        isDetached={isDetached}
      >
        {getMessage()}
        {!isXS && popularPack && (
          <View style={styles.quickTopUpContainer}>
            <Button
              variant="primary"
              text={t('chatInput.credits.getCredits')}
              size="xsmall"
              onPress={handleQuickTopUp}
            />
            <Pressable onPress={handleUpgradePress}>
              <Text size='s' weight='medium' style={styles.link}>
                {t('chatInput.credits.orUpgrade')}
              </Text>
            </Pressable>
          </View>
        )}
        {isXS && (
          <Pressable onPress={handleQuickTopUp}>
            <Text size='s' weight='medium' style={styles.link}>
              {t('chatInput.credits.getCreditsLink')}
            </Text>
          </Pressable>
        )}
      </ChatInputBanner>
    );
  }

  // Free user without stored card - standard upgrade flow
  const getMessage = () => {
    if (totalAvailableCredits <= 0) {
      return isXS 
        ? t('chatInput.credits.outOfCreditsShort') 
        : t('chatInput.credits.outOfCreditsUpgrade');
    }
    return isXS 
      ? t('chatInput.credits.creditsLeft').replace('%COUNT%', creditsLabel) 
      : t('chatInput.credits.runningLowPlus').replace('%COUNT%', creditsLabel);
  };

  return (
    <ChatInputBanner 
      color={totalAvailableCredits <= 0 ? "danger" : "warning"} 
      level={level} 
      isCloseable={isCloseable} 
      isDetached={isDetached}
    >
      {getMessage()}
      <Pressable onPress={handleUpgradePress}>
        <Text size='s' weight='medium' style={styles.link}>
          {totalAvailableCredits <= 0 ? t('chatInput.credits.upgradeNow') : t('chatInput.credits.getFreeMessages')}
        </Text>
      </Pressable>
    </ChatInputBanner>
  );
}

export default React.memo(ChatInputLowCreditsBanner);
